import { getAolItemsForDates } from '../../service-worker/offline_handlers/aol/aol-timeline-utils.js';
import { getEventsFilteredOnEospSops, filterSimpleAolTypeEvents } from '../../service-worker/offline_handlers/aol/aol-item-utils.js';
import * as dateTimeFormatter from 'odfjell-date-time-utils/dateTimeFormatter.js';
import * as dateTimeUtils from 'odfjell-date-time-utils/dateTimeUtils.js';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import { AolItemBM } from '../../types/BusinessModel/AolItemBM';
import { noonReportType } from '../../service-worker/offline_handlers/aol/enums.js';
import { getDstOffsetHours } from '../../service-worker/offline_handlers/aol/utc-converter.js';
import { getFuelRobs } from '../../service-worker/offline_handlers/aol/fuel-rob-data-manager.js';
import { getActivities } from '../../service-worker/offline_handlers/aol/port-log-manager.js';
import { computeClusterData, getPortCluster, type ClusterComputationResult } from './portClusterManager.js';

export interface LocalPortCall {
    imosPortCallId: number;
    port?: any; // TODO: refine with a proper Port type when available
    voyageNumber?: string;
    imosPortFunction?: string;
    portClusterCallId?: number;
    portClusterId?: number;
    berthCallIds?: number[];
}

interface GenerateParams {
    events: TankersEvent[];
    portCalls: LocalPortCall[];
    vessel: any;
    fromDate: Date;
    toDate: Date;
}

const filterAndSortEvents = (events: TankersEvent[]): TankersEvent[] => {
    const validEvents: Array<{ event: TankersEvent; utcTime: number; createdTime: number }> = [];

    for (let i = 0; i < events.length; i++) {
        const e = events[i];
        if (!e.deleted && e.eventDate?.actualDateTimeUtc) {
            validEvents.push({
                event: e,
                utcTime: new Date(e.eventDate.actualDateTimeUtc).getTime(),
                createdTime: new Date(e.created).getTime(),
            });
        }
    }

    validEvents.sort((a, b) => {
        if (a.utcTime !== b.utcTime) return a.utcTime - b.utcTime;
        return a.createdTime - b.createdTime;
    });

    return validEvents.map(({ event }) => event);
};

const removeEventsNotInRotation = (events: TankersEvent[], portCalls: LocalPortCall[]): TankersEvent[] => {
    if (!portCalls.length) return [];
    const validIds = new Set(portCalls.map((p) => p.imosPortCallId));
    return events.filter((e) => validIds.has(e.portCallId));
};

const ensureLocalTimes = (events: TankersEvent[]): TankersEvent[] => {
    for (let i = 0; i < events.length; i++) {
        const e = events[i];
        if (!e.eventDate.actualDateTimeLocal && e.eventDate.actualDateTimeUtc) {
            // NOTE: For correctness, a timezone conversion using port/timeZoneList should be applied.
            e.eventDate.actualDateTimeLocal = e.eventDate.actualDateTimeUtc;
        }
    }
    return events;
};

const buildOfflinePortCalls = (portCalls: LocalPortCall[], events: TankersEvent[]) => {
    const berthCallsByPortCall = new Map<number, Set<number>>();

    for (let i = 0; i < events.length; i++) {
        const ev = events[i];
        if (ev.portCallId && ev.berthCallId) {
            if (!berthCallsByPortCall.has(ev.portCallId)) {
                berthCallsByPortCall.set(ev.portCallId, new Set());
            }
            berthCallsByPortCall.get(ev.portCallId)!.add(ev.berthCallId);
        }
    }

    const result = new Array(portCalls.length);
    for (let i = 0; i < portCalls.length; i++) {
        const pc = portCalls[i];

        const allBerthCallIds = new Set<number>();

        if (pc.berthCallIds) {
            pc.berthCallIds.forEach((id) => allBerthCallIds.add(id));
        }

        const eventBerthCalls = berthCallsByPortCall.get(pc.imosPortCallId);
        if (eventBerthCalls) {
            eventBerthCalls.forEach((id) => allBerthCallIds.add(id));
        }

        const portCallItems = Array.from(allBerthCallIds).map((berthCallId) => ({
            berthCall: { berthCallId },
        }));

        result[i] = {
            portCallId: pc.imosPortCallId,
            portClusterCallId: pc.portClusterCallId,
            portCallItems,
            port: pc.port || { timeZoneList: [] },
        };
    }
    return result;
};

const createOptimizedAolItem = (
    item: any,
    activities: any,
    portCall: LocalPortCall | undefined,
    port: any,
    ordered: TankersEvent[],
    vessel: any,
    cachedTime: string
): AolItemBM =>
    ({
        portLogAolItem: {
            errorEvents: item.errorEvents,
            imosPortCallId: item.imosPortCallId,
            isValid: item.isValid,
            noonReportType: item.noonReportType,
            startTimeLocalTime: dateTimeFormatter.formatDateTime(item.startTimeLocalTime, dateTimeFormatter.DateTimeFormat.queryFormats.dateTimeSS),
            stopTimeLocalTime: dateTimeFormatter.formatDateTime(item.stopTimeLocalTime, dateTimeFormatter.DateTimeFormat.queryFormats.dateTimeSS),
        },
        loadingTimes: activities.loadingTimes,
        unloadingTimes: activities.unloadingTimes,
        idleTime: activities.idleTime,
        shiftingMooringTime: activities.shiftingMooringTime,
        tankCleaningTime: activities.tankCleaningTime,
        dryDockingTime: activities.dryDockingTime,
        anchorageTime: activities.anchorageTime,
        steamingForTankCleaningTime: activities.steamingForTankCleaningTime,
        canalTransitTime: activities.canalTransitTime,
        operationalTargetSpeedTime: activities.operationalTargetSpeedTime,
        operationalTargetConsumptionTime: activities.operationalTargetConsumptionTime,
        operationalNonTargetTime: activities.operationalNonTargetTime,
        operationalAwaitingOrdersOrDriftingTime: activities.operationalAwaitingOrdersOrDriftingTime,
        operationalTankPreparationTime: activities.operationalTankPreparationTime,
        navigationalEcaZoneTime: activities.navigationalEcaZoneTime,
        navigationalCasualtyAndSarTime: activities.navigationalCasualtyAndSarTime,
        navigationalHighRiskAreaTime: activities.navigationalHighRiskAreaTime,
        navigationalPilotManeuveringTime: activities.navigationalPilotManeuveringTime,
        navigationalRiverPortManeuveringTime: activities.navigationalRiverPortManeuveringTime,
        navigationalSafetyManeuveringTime: activities.navigationalSafetyManeuveringTime,
        navigationalStormShelteringTime: activities.navigationalStormShelteringTime,
        navigationalTrafficManeuveringTime: activities.navigationalTrafficManeuveringTime,
        navigationalCasualtyAndSarDrillTime: activities.navigationalCasualtyAndSarDrillTime,
        technicalAuxiliarySystemFailureTime: activities.technicalAuxiliarySystemFailureTime,
        technicalFuelQualityIssueTime: activities.technicalFuelQualityIssueTime,
        technicalOtherTechnicalIssueTime: activities.technicalOtherTechnicalIssueTime,
        technicalPropulsionSystemFailureTime: activities.technicalPropulsionSystemFailureTime,
        timeZoneChangeHours: activities.timeZoneChangeHours,
        portDescription: port?.name ?? '???',
        dstOffsetHours: getDstOffsetHours(port, item.startTimeLocalTime, item.stopTimeLocalTime),
        fuelRobDatas: getFuelRobs(port, item.startTimeLocalTime, item.stopTimeLocalTime, ordered, vessel),
        voyageNumber: portCall?.voyageNumber ?? '',
        cachedTime,
        cargoTankTemperatures: [],
        navtorDistance: 0,
        cargoOnBoard: null,
    } as unknown as AolItemBM);

const enrichEventsWithPortClusterData = (events: TankersEvent[], clusterResult: ClusterComputationResult): void => {
    for (let i = 0; i < events.length; i++) {
        const ev = events[i];
        if (ev.portCallId !== null && ev.portCallId !== undefined) {
            const clusterData = getPortCluster(clusterResult, ev.portCallId);
            if (clusterData) {
                ev.portClusterCallId = clusterData.portClusterCallId;
                ev.portClusterId = clusterData.portClusterId ?? undefined;
            }
        }
    }
};

const adjustNoonReportType = (item: any, portCall: LocalPortCall | undefined): void => {
    if (
        portCall?.imosPortFunction === 'I' &&
        item.isValid &&
        item.noonReportType !== noonReportType.port &&
        item.noonReportType !== noonReportType.sea
    ) {
        item.noonReportType = noonReportType.port;
    }
};

export const generateLocalAolItems = ({ events, portCalls, vessel, fromDate, toDate }: GenerateParams): AolItemBM[] => {
    if (!fromDate || !toDate) return [];
    if (toDate < fromDate) return [];

    const baseEvents = Array.isArray(events) ? events : [];

    let ordered = filterAndSortEvents(baseEvents);
    ordered = removeEventsNotInRotation(ordered, portCalls);
    ordered = ensureLocalTimes(ordered);

    const clusterResult = computeClusterData(portCalls, ordered);

    for (let i = 0; i < portCalls.length; i++) {
        const pc = portCalls[i];
        const clusterData = getPortCluster(clusterResult, pc.imosPortCallId);
        if (clusterData) {
            pc.portClusterCallId = clusterData.portClusterCallId;
            pc.portClusterId = clusterData.portClusterId ?? undefined;
        }
    }

    enrichEventsWithPortClusterData(ordered, clusterResult);

    const isSimpleMode = vessel?.hasOrcaWebSimpleMode ?? false;
    const filteredEospSosp = isSimpleMode ? filterSimpleAolTypeEvents(ordered) : getEventsFilteredOnEospSops(ordered);

    enrichEventsWithPortClusterData(filteredEospSosp, clusterResult);

    const offlinePortCalls = buildOfflinePortCalls(portCalls, ordered);
    const generatedMap = getAolItemsForDates(filteredEospSosp, offlinePortCalls, fromDate, toDate, isSimpleMode);

    const portCallsMap = new Map(portCalls.map((pc) => [pc.imosPortCallId, pc]));

    // Pre-allocate array capacity estimation to reduce reallocations
    const estimatedItemCount = Math.ceil((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000)) * 2;
    const result: AolItemBM[] = new Array(estimatedItemCount);
    let resultIndex = 0;

    // Cache frequently used values
    const cachedTime = String(Date.now());

    let iterDate = new Date(fromDate.getTime());
    while (dateTimeUtils.isDateSameOrBefore(iterDate, toDate)) {
        const key = dateTimeFormatter.formatDateTime(iterDate, 'yyyy-MM-ddTHH:mm');
        const items = generatedMap[key];

        if (items && items.length > 0) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];

                const activities = getActivities(
                    ordered,
                    filteredEospSosp,
                    item.startTimeLocalTime,
                    item.stopTimeLocalTime,
                    item.imosPortCallId,
                    portCalls
                );

                const portCall = portCallsMap.get(item.imosPortCallId);
                const port = portCall?.port ? { ...portCall.port, timeZoneList: portCall.port.timeZoneList || [] } : undefined;

                adjustNoonReportType(item, portCall);

                // Create AOL item with cached values for better performance
                result[resultIndex++] = createOptimizedAolItem(item, activities, portCall, port, ordered, vessel, cachedTime);
            }
        }

        iterDate = dateTimeUtils.addDateDays(iterDate, 1);
    }

    // Trim array to actual size
    result.length = resultIndex;
    return result;
};
